import type { HTMLAttributes, ReactNode } from 'react';
import React, { useMemo } from 'react';

import { DropdownMenuGroup } from '@radix-ui/react-dropdown-menu';
import type { Table } from '@tanstack/react-table';
import clsx from 'clsx';
import { z } from 'zod';

import { Funnel, X } from '@remarkable/ark-icons';
import { ButtonClicked } from 'src/ampli';
import { tracker } from 'src/analytics/tracker';
import {
  Divider,
  Tooltip,
  TooltipContent,
  TooltipRoot,
  TooltipTrigger,
} from 'src/components';
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuLabel,
} from 'src/components/DropdownMenu';

export function FleetTableColumnFilterSelector<T>({
  table,
  columns,
  className,
  componentLocation,
  ...props
}: HTMLAttributes<HTMLDivElement> & {
  table: Table<T>;
  columns: string[];
  componentLocation: string;
}) {
  const numberOfFilters = useMemo(() => {
    return table
      .getState()
      .columnFilters.reduce(
        (acc, current) =>
          acc + (Array.isArray(current.value) ? current.value.length : 1),
        0
      );
  }, [table.getState().columnFilters]);

  const availableFilters = table
    .getAllColumns()
    .filter((column) => columns.includes(column.id));

  return (
    <div className={clsx('flex items-center gap-2', className)} {...props}>
      <TooltipRoot>
        <TooltipContent>Filter</TooltipContent>
        <DropdownMenu
          trigger={
            <TooltipTrigger asChild>
              <button
                aria-label="Filter"
                className={clsx(
                  'rounded-sm h-48 px-16 outline-hidden transition-all hover:bg-neutral-light-4 focus:bg-pen-blue-light data-[state=open]:bg-neutral-light-5 data-[state=open]:hover:bg-neutral-light-6',
                  {
                    'bg-neutral-light-3': numberOfFilters > 0,
                  }
                )}
                onClick={() => {
                  tracker.trackEvent(
                    new ButtonClicked({
                      component_location: componentLocation,
                      text: '[funnel icon]',
                      action: 'open column filters menu',
                    })
                  );
                }}
              >
                <span className="flex items-center gap-8">
                  <Funnel className="!size-24" />
                  {numberOfFilters > 0 && (
                    <span className="interface-md font-regular text-muted">
                      ({numberOfFilters})
                    </span>
                  )}
                </span>
              </button>
            </TooltipTrigger>
          }
          data-cy="filter-content"
        >
          {availableFilters.map((column, index) => {
            return (
              <React.Fragment key={column.id}>
                {index > 0 && (
                  <Divider className="mx-16 h-[1px] bg-neutral-light-4" />
                )}
                <DropdownMenuGroup key={column.id}>
                  <DropdownMenuLabel>
                    {column.columnDef.header as ReactNode}
                  </DropdownMenuLabel>

                  <div className="max-h-[300px] overflow-y-auto">
                    {Array.from(column.getFacetedUniqueValues().entries()).map(
                      ([name, count]) => {
                        if (typeof name !== 'string') {
                          return null;
                        }
                        const unknownFilterValue =
                          column.getFilterValue() ?? [];

                        const parseResult = z
                          .array(z.string())
                          .safeParse(unknownFilterValue);
                        const filterValue = parseResult.success
                          ? parseResult.data
                          : [];

                        return (
                          <DropdownMenuCheckboxItem
                            checked={filterValue.includes(name)}
                            key={name}
                            onClick={(e) => {
                              tracker.trackEvent(
                                new ButtonClicked({
                                  component_location: componentLocation,
                                  text: name,
                                  action: filterValue.includes(name)
                                    ? 'remove column filter'
                                    : 'add column filter',
                                })
                              );
                              e.stopPropagation();
                              const currentFilterValue =
                                (column.getFilterValue() as string[]) ?? [];

                              column.setFilterValue(() => {
                                if (currentFilterValue.includes(name)) {
                                  return currentFilterValue.filter(
                                    (v) => v !== name
                                  );
                                }
                                const newFilters = [
                                  ...currentFilterValue,
                                  name,
                                ];
                                return newFilters;
                              });
                            }}
                          >
                            {name}{' '}
                            <span className="ml-4 text-neutral-dark-1">
                              ({count})
                            </span>
                          </DropdownMenuCheckboxItem>
                        );
                      }
                    )}
                  </div>
                </DropdownMenuGroup>
              </React.Fragment>
            );
          })}
        </DropdownMenu>
      </TooltipRoot>
      {numberOfFilters > 0 && (
        <Tooltip title="Clear all filters" asChild>
          <button
            className="rounded-sm h-48 bg-neutral-light-3 px-16 outline-hidden transition-all hover:bg-neutral-light-4 focus:bg-pen-blue-light"
            onClick={() => {
              table.resetColumnFilters();
            }}
            data-cy="clear-filters-button"
            aria-label="Clear all column filters"
          >
            <X />
          </button>
        </Tooltip>
      )}
    </div>
  );
}
